import type { TFunction } from 'i18next';

import type { NotificationOptions } from '../components/feedback/NotificationSystem';
import type { ErrorDetails } from './errorHandling';

const I18N_KEY_PATTERN = /^[a-z][a-zA-Z0-9]*(?:\.[a-zA-Z0-9]+)+$/;

/** Map known English API / runtime error strings to notifications namespace keys. */
const MESSAGE_PATTERNS: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /no internet connection/i, key: 'errors.networkOfflineCheck' },
  { pattern: /failed to connect to server/i, key: 'errors.networkConnectionFailed' },
  { pattern: /unable to connect to the server/i, key: 'errors.serverUnreachable' },
  { pattern: /network error/i, key: 'networkError' },
  { pattern: /failed to fetch/i, key: 'networkError' },
  { pattern: /connection error/i, key: 'subscription.connectionError' },
  { pattern: /request timed out|timed out/i, key: 'errors.networkTimeout' },
  { pattern: /server error/i, key: 'errors.serverError' },
  { pattern: /an unexpected error occurred/i, key: 'errors.unexpected' },
  { pattern: /an error occurred/i, key: 'genericError' },
  { pattern: /operation completed successfully/i, key: 'errors.operationSuccess' },
  { pattern: /requires an internet connection/i, key: 'errors.requiresInternet' },
  { pattern: /network error while loading audio/i, key: 'audio.networkError' },
  { pattern: /failed to submit form/i, key: 'form.submitFailed' },
  { pattern: /form submitted successfully/i, key: 'form.submitSuccess' },
];

export function resolveNotificationMessage(
  messageOrKey: string | undefined,
  t: TFunction,
  ns: string = 'notifications'
): string {
  if (!messageOrKey?.trim()) {
    return messageOrKey ?? '';
  }

  const trimmed = messageOrKey.trim();

  if (I18N_KEY_PATTERN.test(trimmed)) {
    const translated = t(trimmed, { ns, defaultValue: '' });
    if (translated) {
      return translated;
    }
  }

  for (const { pattern, key } of MESSAGE_PATTERNS) {
    if (pattern.test(trimmed)) {
      const translated = t(key, { ns, defaultValue: '' });
      if (translated) {
        return translated;
      }
    }
  }

  return trimmed;
}

export function resolveNotificationOptions(
  options: NotificationOptions | undefined,
  t: TFunction,
  ns: string = 'notifications'
): NotificationOptions | undefined {
  if (!options) {
    return options;
  }

  return {
    ...options,
    title: options.title ? resolveNotificationMessage(options.title, t, ns) : options.title,
    persianTitle: undefined,
    persianMessage: undefined,
    actions: options.actions?.map((action) => ({
      ...action,
      label: resolveNotificationMessage(action.label, t, ns),
      persianLabel: undefined,
    })),
  };
}

/** Prefer a translation key for normalized error details when the message is a known built-in English string. */
export function getErrorMessageKey(error: ErrorDetails): string | null {
  if (error.code === 'NETWORK_TIMEOUT') {
    return 'errors.networkTimeout';
  }

  if (error.code === 'NETWORK_ERROR' || error.code?.includes('NETWORK')) {
    if (!navigator.onLine) {
      return 'errors.networkOfflineCheck';
    }
    return 'errors.networkConnectionFailed';
  }

  if (error.code === 'VALIDATION_ERROR' || error.code?.includes('VALIDATION')) {
    return null;
  }

  if (error.code === 'UNKNOWN_ERROR') {
    return 'genericError';
  }

  return null;
}
